import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  applySimpleLoginUserPayload,
  fetchSimpleLoginUserRaw,
  loginUserPermissionsFromRaw,
} from '@/api/auth'
import { isLoginSuperAdmin } from '@/config/superAdmin'
import { useUserProfileStore } from '@/stores/userProfile'

export const usePermissionStore = defineStore('permission', () => {
  const loaded = ref(false)
  const loading = ref(false)
  /** 接口失败时不强刷为空，避免误藏按钮；与 loadFailed 配合 */
  const loadFailed = ref(false)
  const codeSet = ref<Set<string>>(new Set())
  const roleCodeSet = ref<Set<string>>(new Set())
  /** 当前登录账号（与 /auth/b/getSimpleLoginUser 一致） */
  const loginAccount = ref('')
  let loadPromise: Promise<void> | null = null

  async function loadCodes() {
    if (loaded.value) return
    if (loadPromise) return loadPromise
    loading.value = true
    loadFailed.value = false
    loadPromise = (async () => {
      try {
        const raw = await fetchSimpleLoginUserRaw()
        const userProfileStore = useUserProfileStore()
        userProfileStore.setProfile(applySimpleLoginUserPayload(raw))
        const { buttonCodeList, roleCodeList, account } = loginUserPermissionsFromRaw(raw)
        loginAccount.value = account
        codeSet.value = new Set(buttonCodeList.map((x) => x.trim()).filter(Boolean))
        roleCodeSet.value = new Set(roleCodeList.map((x) => x.trim()).filter(Boolean))
      } catch {
        // 已有权限集合时保留严格校验；仅首次拉取失败时放行，避免接口故障导致整站按钮全禁
        loadFailed.value = codeSet.value.size === 0
      } finally {
        loaded.value = true
        loading.value = false
        loadPromise = null
      }
    })()
    return loadPromise
  }

  /** 强制重新拉取按钮权限码（按钮增删 / 角色授权后调用） */
  async function reloadCodes() {
    reset()
    await loadCodes()
  }

  function reset() {
    loaded.value = false
    loading.value = false
    loadFailed.value = false
    codeSet.value = new Set()
    roleCodeSet.value = new Set()
    loginAccount.value = ''
    loadPromise = null
  }

  function hasRole(roleCode: string): boolean {
    const c = roleCode.trim()
    if (!c) return false
    if (loadFailed.value) return false
    return roleCodeSet.value.has(c)
  }

  const isSuperAdminComputed = computed(() =>
    loadFailed.value ? false : isLoginSuperAdmin(loginAccount.value, roleCodeSet.value),
  )

  /** 与后端超管数据范围一致：内置账号或 superAdmin 角色编码 */
  function isSuperAdmin() {
    return isSuperAdminComputed.value
  }

  function hasPerm(code: string): boolean {
    const c = code.trim()
    if (!c) return true
    if (loadFailed.value) return true
    return codeSet.value.has(c)
  }

  return {
    loaded,
    loading,
    loadFailed,
    codeSet,
    roleCodeSet,
    loginAccount,
    loadCodes,
    reloadCodes,
    reset,
    hasPerm,
    hasRole,
    isSuperAdmin,
  }
})
